import { InvestorRequestPaymentMethod } from "@investorRequest/models";

export type ExtractionConfidence = "high" | "low" | "missing";

export type DetectedPaymentMethod = InvestorRequestPaymentMethod | "unknown";

export type PaymentRail = "ach" | "fedwire" | "swift";

export interface ExtractionFieldValue {
  value: string | null;
  confidence: ExtractionConfidence;
  candidates?: string[] | null;
  providerConfidence?: ExtractionConfidence | null;
}

/** Shared domestic-rail wire fields (ACH and Fedwire) — 13 fields, no IBAN. */
export interface DomesticExtractionFields {
  beneficiaryAccountName: ExtractionFieldValue;
  bankName: ExtractionFieldValue;
  routingNumber: ExtractionFieldValue;
  accountNumber: ExtractionFieldValue;
  swiftCode: ExtractionFieldValue;
  intermediaryYesNo: ExtractionFieldValue;
  intermediarySwiftOrAba: ExtractionFieldValue;
  intermediaryRoutingNumber: ExtractionFieldValue;
  intermediaryPaymentMessage: ExtractionFieldValue;
  furtherCreditYesNo: ExtractionFieldValue;
  furtherCreditLine1: ExtractionFieldValue;
  furtherCreditLine2: ExtractionFieldValue;
  furtherCreditLine3: ExtractionFieldValue;
}

/** SWIFT / international rail fields — domestic set plus IBAN. */
export interface SwiftExtractionFields extends DomesticExtractionFields {
  iban: ExtractionFieldValue;
}

export type AchExtractionFields = DomesticExtractionFields;
export type FedwireExtractionFields = DomesticExtractionFields;

export interface ExtractionFieldsByRail {
  ach?: AchExtractionFields | null;
  fedwire?: FedwireExtractionFields | null;
  swift?: SwiftExtractionFields | null;
}

export type ResolvedExtractionFields =
  | { rail: "ach"; fields: AchExtractionFields }
  | { rail: "fedwire"; fields: FedwireExtractionFields }
  | { rail: "swift"; fields: SwiftExtractionFields };

export interface ExtractionResult {
  extractionId: string;
  provider: string;
  detectedPaymentMethod: DetectedPaymentMethod;
  fieldsByRail: ExtractionFieldsByRail;
}

export interface UploadUrlResponse {
  url: string;
  fields: Record<string, string>;
  objectKey: string;
}

export type BankExtractionPhase = "idle" | "processing" | "review" | "totalFailure";

export interface BankExtractionState {
  phase: BankExtractionPhase;
  processingStep: number;
  extractionResult: ExtractionResult | null;
  filename: string | null;
  objectKey: string | null;
  prefillBannerDismissed: boolean;
  methodMismatchDismissed: boolean;
  extractionEditedFields: Record<string, boolean>;
  ambiguousAcknowledgedFields: Record<string, boolean>;
  originalExtractedValues: Record<string, string | null>;
  fieldConfidence: Record<string, ExtractionConfidence>;
  fileError: string | null;
}

export const initialBankExtractionState: BankExtractionState = {
  phase: "idle",
  processingStep: 0,
  extractionResult: null,
  filename: null,
  objectKey: null,
  prefillBannerDismissed: false,
  methodMismatchDismissed: false,
  extractionEditedFields: {},
  ambiguousAcknowledgedFields: {},
  originalExtractedValues: {},
  fieldConfidence: {},
  fileError: null,
};
